import type { CashSummaryDto } from '@hbs-us/contracts';
import { useTranslation } from '@hbs-us/ui';
import { isHidden, type ReportFormatters } from '../useReportFormatters.js';

/**
 * P&L summary stat grid (legacy ReportsPage 135–159): Revenue · Total Costs ·
 * Net Profit · Cash Collected. CSS grid + region/list semantics (NOT a layout
 * <table> — A11Y-01). Numerals render in IBM Plex Mono (`font-mono`). Net Profit
 * uses the green/red status token by SIGN — and the meaning is ALSO carried by
 * the number's own `-`/value, never color alone (A11Y-01 color-not-alone).
 */

function StatCard({
  label,
  value,
  sub,
  valueClassName,
}: {
  label: string;
  value: string;
  sub: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-6">
      <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </div>
      <div
        className={`mt-2 font-mono font-semibold text-foreground text-xl ${valueClassName ?? ''}`}
      >
        {value}
      </div>
      <div className="mt-1 text-muted-foreground text-xs">{sub}</div>
    </div>
  );
}

export function PnlStatGrid({ cash, fmt }: { cash: CashSummaryDto; fmt: ReportFormatters }) {
  const { t } = useTranslation();

  const revenue = Number(cash.recordedIncome || 0);
  // Costs include wages. When the server hides them, so are costs and profit: working profit out
  // from revenue alone printed the whole revenue as profit at a 100% margin.
  const hidden = isHidden(cash.cashOutflow as number | null);
  const costs = hidden ? null : Number(cash.cashOutflow || 0);
  const netProfit = costs === null ? null : revenue - costs;
  const margin = netProfit === null ? null : revenue > 0 ? (netProfit / revenue) * 100 : 0;
  const collected = Number(cash.collectedPayments || 0);
  const outstanding = Number(cash.openReceivables || 0);
  const invoiced = Number(cash.invoicedAmount || 0);

  return (
    <section
      aria-label={t('reports:title')}
      className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <StatCard
        label={t('reports:pnl.revenue')}
        value={fmt.money(revenue)}
        sub={t('reports:pnl.revenueSub', { amount: fmt.money(invoiced) })}
      />
      <StatCard
        label={t('reports:pnl.totalCosts')}
        value={fmt.money(costs)}
        sub={hidden ? t('reports:pnl.hidden') : t('reports:pnl.totalCostsSub')}
      />
      <StatCard
        label={t('reports:pnl.netProfit')}
        value={fmt.money(netProfit)}
        valueClassName={
          netProfit === null ? undefined : netProfit >= 0 ? 'text-success' : 'text-destructive'
        }
        sub={
          netProfit === null
            ? t('reports:pnl.hidden')
            : t('reports:pnl.netProfitSub', { percent: fmt.percent(margin) })
        }
      />
      <StatCard
        label={t('reports:pnl.cashCollected')}
        value={fmt.money(collected)}
        sub={t('reports:pnl.cashCollectedSub', { amount: fmt.money(outstanding) })}
      />
    </section>
  );
}
